import React, { useState, useEffect } from 'react';
import { CheckCircle, Info, Plus, Minus, Gift, Calculator, Tag } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';

const PriceSummary = ({ bookingData, priceData, onAddonsChange }) => {
  // Add-ons state
  const [selectedAddons, setSelectedAddons] = useState(bookingData.selectedAddons || []);
  const [sapTotalUValues, setSapTotalUValues] = useState(bookingData.sapTotalUValues || 3);
  
  // Determine which add-ons are available based on primary service
  const epcServices = ['domestic_epc', 'domestic_advice', 'commercial_warehouse', 'commercial_hotel_office', 'commercial_advice'];
  const commercialServices = ['commercial_warehouse', 'commercial_hotel_office', 'commercial_advice'];
  const isEpcService = epcServices.includes(bookingData.serviceId);
  const isCommercialService = commercialServices.includes(bookingData.serviceId);
  const isSapService = bookingData.serviceId === 'sap_calculation';
  
  // Available add-ons for EPC services (prices vary by service type)
  const getAvailableAddons = () => {
    if (isCommercialService) {
      // Commercial EPC - sqm-based pricing
      return [
        {
          id: 'marketing_plans',
          name: 'Marketing Floor Plans',
          description: 'High-quality floor plans for property marketing and sales',
          basePrice: 120,
          pricingType: 'sqm',
          includedSqm: 150,
          extraRate: 0.80
        },
        {
          id: 'leasehold_plans',
          name: 'Lease Plans (To-Scale)',
          description: 'Accurate to-scale plans including floor plan',
          basePrice: 240,
          upgradePrice: 120, // Price if floor plan already selected
          pricingType: 'sqm',
          includedSqm: 150,
          extraRate: 0.80,
          includesFloorPlan: true
        }
      ];
    }
    // Domestic EPC - fixed pricing
    return [
      {
        id: 'marketing_plans',
        name: 'Marketing Floor Plans',
        description: 'High-quality floor plans for property marketing and sales',
        basePrice: 120,
        pricingType: 'fixed'
      },
      {
        id: 'leasehold_plans',
        name: 'Lease Plans (To-Scale)',
        description: 'Accurate to-scale plans including floor plan',
        basePrice: 240,
        upgradePrice: 120, // Price if floor plan already selected
        pricingType: 'fixed',
        includesFloorPlan: true
      }
    ];
  };
  
  const availableAddons = getAvailableAddons();
  
  // Handle addon toggle
  const handleAddonToggle = (addonId) => {
    const newAddons = selectedAddons.includes(addonId)
      ? selectedAddons.filter(id => id !== addonId)
      : [...selectedAddons, addonId];
    
    setSelectedAddons(newAddons);
    if (onAddonsChange) {
      onAddonsChange({ selectedAddons: newAddons, sapTotalUValues });
    }
  };
  
  // Handle SAP U-Value quantity change
  const handleUValueChange = (delta) => {
    const newValue = Math.max(3, sapTotalUValues + delta); // Minimum 3 (included)
    setSapTotalUValues(newValue);
    if (onAddonsChange) {
      onAddonsChange({ selectedAddons, sapTotalUValues: newValue });
    }
  };
  
  // Calculate addon prices based on service type
  const calculateAddonPrice = (addonId) => {
    const addon = availableAddons.find(a => a.id === addonId);
    if (!addon) return 0;
    
    // Check if this is a lease plan and floor plan is already selected
    const floorPlanSelected = selectedAddons.includes('marketing_plans');
    
    if (addon.pricingType === 'sqm') {
      // Commercial EPC - sqm-based pricing
      const sqm = bookingData.sqm || 150;
      let basePrice = addon.basePrice;
      
      // If lease plan and floor plan already selected, use upgrade price
      if (addonId === 'leasehold_plans' && floorPlanSelected && addon.upgradePrice) {
        basePrice = addon.upgradePrice;
      }
      
      if (sqm <= addon.includedSqm) {
        return basePrice;
      }
      const extraSqm = sqm - addon.includedSqm;
      return basePrice + (extraSqm * addon.extraRate);
    }
    
    // Domestic EPC - fixed pricing
    if (addon.pricingType === 'fixed') {
      // If lease plan and floor plan already selected, use upgrade price
      if (addonId === 'leasehold_plans' && floorPlanSelected && addon.upgradePrice) {
        return addon.upgradePrice;
      }
      return addon.basePrice;
    }
    
    // Legacy room-based pricing (fallback)
    let price = addon.basePrice;
    const rooms = bookingData.rooms || 5;
    if (rooms > 5) {
      price += (rooms - 5) * 5; // £5 per additional room
    }
    return price;
  };
  
  // Get pricing description for addon
  const getAddonPriceDescription = (addonId) => {
    const addon = availableAddons.find(a => a.id === addonId);
    if (!addon) return null;
    
    const floorPlanSelected = selectedAddons.includes('marketing_plans');
    
    if (addon.pricingType === 'sqm') {
      const sqm = bookingData.sqm || 150;
      
      // Show upgrade info for lease plan
      if (addonId === 'leasehold_plans' && addon.includesFloorPlan) {
        if (floorPlanSelected) {
          return `Upgrade from floor plan (+£${addon.upgradePrice})`;
        }
        return `Includes floor plan`;
      }
      
      if (sqm <= addon.includedSqm) {
        return `Up to ${addon.includedSqm}m²`;
      }
      const extraSqm = sqm - addon.includedSqm;
      return `${addon.includedSqm}m² + ${extraSqm}m² @ £${addon.extraRate.toFixed(2)}/m²`;
    }
    
    if (addon.pricingType === 'fixed') {
      // Show upgrade info for lease plan
      if (addonId === 'leasehold_plans' && addon.includesFloorPlan) {
        if (floorPlanSelected) {
          return `Upgrade from floor plan (+£${addon.upgradePrice})`;
        }
        return `Includes floor plan`;
      }
      return null;
    }
    
    const rooms = bookingData.rooms || 5;
    if (rooms > 5) {
      return `Based on ${rooms} rooms`;
    }
    return null;
  };
  
  // Calculate SAP extra U-Values price
  const sapIncludedUValues = 3;
  const sapExtraUValues = Math.max(0, sapTotalUValues - sapIncludedUValues);
  const sapUValueUnitRate = 65; // £65 per additional U-Value
  const sapExtraUValuesPrice = sapExtraUValues * sapUValueUnitRate;
  
  // Calculate totals
  const addonsSubtotal = selectedAddons.reduce((sum, id) => sum + calculateAddonPrice(id), 0);
  const bundleDiscountPercent = isEpcService && selectedAddons.length > 0 ? 10 : 0;
  const subtotalForDiscount = (priceData?.base_price || 0) + addonsSubtotal;
  const bundleDiscount = bundleDiscountPercent > 0 ? Math.round(subtotalForDiscount * 0.10 * 100) / 100 : 0;
  
  // Use priceData values if available (from recalculation), otherwise calculate locally
  const displayAddonsPrice = priceData?.addons_price ?? addonsSubtotal;
  const displayBundleDiscount = priceData?.bundle_discount ?? bundleDiscount;
  const displaySapExtraPrice = priceData?.sap_extra_u_values_price ?? sapExtraUValuesPrice;

  return (
    <div data-testid="price-summary-step">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Price Summary</h2>
      <p className="text-gray-600 mb-6">Review your booking details and pricing</p>
      
      {/* Main Service Summary */}
      <Card className="border-none shadow-md mb-6">
        <CardContent className="p-6">
          <h3 className="font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Your Selected Service
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Service</span>
              <span className="font-medium">{bookingData.serviceName}</span>
            </div>
            
            {bookingData.rooms && (
              <div className="flex justify-between">
                <span className="text-gray-600">Rooms/Zones</span>
                <span className="font-medium">{bookingData.rooms}</span>
              </div>
            )}
            
            {bookingData.sqm && (
              <div className="flex justify-between">
                <span className="text-gray-600">Floor Area</span>
                <span className="font-medium">{bookingData.sqm} m²</span>
              </div>
            )}
            
            <div className="flex justify-between">
              <span className="text-gray-600">Property</span>
              <span className="font-medium">{bookingData.postcode}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Appointment</span>
              <span className="font-medium">
                {bookingData.preferredDate} at {bookingData.preferredTime}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Add Services Section - EPC Services */}
      {isEpcService && (
        <Card className="border-2 border-dashed border-green-300 shadow-md mb-6 bg-green-50/30">
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg text-gray-900 mb-2 flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-600" />
              Add Services to This Booking
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Bundle additional services and save 10% on your combined booking.
            </p>
            
            <div className="space-y-4">
              {availableAddons.map(addon => {
                const addonPrice = calculateAddonPrice(addon.id);
                const isSelected = selectedAddons.includes(addon.id);
                
                return (
                  <div 
                    key={addon.id}
                    className={`flex items-start gap-3 p-4 rounded-lg border transition-all cursor-pointer ${
                      isSelected 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-white border-gray-200 hover:border-green-200'
                    }`}
                    onClick={() => handleAddonToggle(addon.id)}
                  >
                    <Checkbox 
                      id={addon.id}
                      checked={isSelected}
                      onCheckedChange={(e) => {
                        // Prevent double trigger - the parent div already handles the click
                        e?.stopPropagation?.();
                      }}
                      className="mt-1 pointer-events-none"
                    />
                    <div className="flex-1">
                      <Label htmlFor={addon.id} className="font-medium text-gray-900 cursor-pointer">
                        {addon.name}
                      </Label>
                      <p className="text-sm text-gray-600 mt-1">{addon.description}</p>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold text-green-600">£{addonPrice.toFixed(2)}</span>
                      {getAddonPriceDescription(addon.id) && (
                        <p className="text-xs text-gray-500">{getAddonPriceDescription(addon.id)}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {selectedAddons.length > 0 && (
              <Alert className="mt-4 bg-green-100 border-green-300">
                <Gift className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <strong>Bundle Discount Applied!</strong> You&apos;re saving 10% on your combined booking.
                </AlertDescription>
              </Alert>
            )}
            
            {/* Elevations & Sections for Lease Plan add-on */}
            {selectedAddons.includes('leasehold_plans') && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Optional Extras for Lease Plan</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="addon-elevations" className="text-sm text-gray-600">
                      Elevations (£65 each)
                    </Label>
                    <Input
                      id="addon-elevations"
                      type="number"
                      min="0"
                      max="10"
                      value={bookingData.elevations || 0}
                      onChange={(e) => onAddonsChange({ elevations: parseInt(e.target.value) || 0 })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="addon-sections" className="text-sm text-gray-600">
                      Sections (£65 each)
                    </Label>
                    <Input
                      id="addon-sections"
                      type="number"
                      min="0"
                      max="5"
                      value={bookingData.sections || 0}
                      onChange={(e) => onAddonsChange({ sections: parseInt(e.target.value) || 0 })}
                      className="mt-1"
                    />
                  </div>
                </div>
                {((bookingData.elevations || 0) > 0 || (bookingData.sections || 0) > 0) && (
                  <p className="text-sm text-green-600 mt-2">
                    Extras: £{(((bookingData.elevations || 0) + (bookingData.sections || 0)) * 65).toFixed(2)}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Add U-Values Section - SAP Service */}
      {isSapService && (
        <Card className="border-2 border-dashed border-blue-300 shadow-md mb-6 bg-blue-50/30">
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg text-gray-900 mb-2 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-blue-600" />
              Additional U-Value Calculations
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Your SAP Calculation includes 3 U-Values. Add more if needed.
            </p>
            
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label className="font-medium text-gray-900">Total U-Values Required</Label>
                  <p className="text-sm text-gray-600">3 included in base price</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleUValueChange(-1)}
                    disabled={sapTotalUValues <= 3}
                    className="h-8 w-8 p-0"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-xl font-bold text-gray-900 w-8 text-center">
                    {sapTotalUValues}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleUValueChange(1)}
                    className="h-8 w-8 p-0"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Included U-Values</span>
                  <span className="text-green-600 font-medium">3 (no extra charge)</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Additional U-Values</span>
                  <span className="font-medium">{sapExtraUValues}</span>
                </div>
                {sapExtraUValues > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Extra Cost ({sapExtraUValues} × £{sapUValueUnitRate})
                    </span>
                    <span className="font-semibold text-blue-600">
                      £{displaySapExtraPrice.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Price Breakdown */}
      <Card className="border-none shadow-md">
        <CardContent className="p-6">
          <h3 className="font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
            <Tag className="w-5 h-5 text-green-600" />
            Price Breakdown
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Base Service Price</span>
              <span className="font-medium">£{(priceData?.base_price || 0).toFixed(2)}</span>
            </div>
            
            {priceData?.extras_price > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Extras</span>
                <span className="font-medium">£{priceData.extras_price.toFixed(2)}</span>
              </div>
            )}
            
            {/* Add-ons for EPC */}
            {isEpcService && displayAddonsPrice > 0 && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Add-on Services</span>
                  <span className="font-medium">£{displayAddonsPrice.toFixed(2)}</span>
                </div>
                {selectedAddons.map(addonId => {
                  const addon = availableAddons.find(a => a.id === addonId);
                  return addon ? (
                    <div key={addonId} className="flex justify-between text-sm pl-4">
                      <span className="text-gray-500">• {addon.name}</span>
                      <span className="text-gray-500">£{calculateAddonPrice(addonId).toFixed(2)}</span>
                    </div>
                  ) : null;
                })}
              </>
            )}
            
            {/* SAP U-Value extras */}
            {isSapService && displaySapExtraPrice > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Additional U-Values ({sapExtraUValues})</span>
                <span className="font-medium">£{displaySapExtraPrice.toFixed(2)}</span>
              </div>
            )}
            
            {/* Bundle Discount */}
            {displayBundleDiscount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Bundle Discount (10%)</span>
                <span className="font-medium">-£{displayBundleDiscount.toFixed(2)}</span>
              </div>
            )}
            
            {priceData?.travel_surcharge > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">
                  Travel Surcharge
                  {priceData.drive_time_minutes && (
                    <span className="text-sm text-gray-500 ml-1">
                      ({priceData.drive_time_minutes} min drive)
                    </span>
                  )}
                </span>
                <span className="font-medium">£{priceData.travel_surcharge.toFixed(2)}</span>
              </div>
            )}
            
            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-green-600">
                  £{(priceData?.total_price || 0).toFixed(2)}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">No VAT to pay</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Payment Info */}
      <Alert className="mt-6 bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Payment Method:</strong> {bookingData.paymentMethod === 'bacs' ? 'Bank Transfer (BACS)' : 'Cash on Completion'}
          <br />
          <span className="text-sm">
            {bookingData.paymentMethod === 'bacs' 
              ? 'Account details will be provided in your confirmation email.' 
              : 'Payment due on completion of service.'}
          </span>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default PriceSummary;
